import { useState, useEffect, useCallback, useRef } from "react";

// Retorna a data civil local do navegador no formato AAAA-MM-DD
export function getLocalDateKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Persistência local no navegador (sem cadastro, sem backend).
// Trata troca de chave em tempo de execução sem sobrescrever dados do dia/semana seguinte com dados anteriores.
export function useLocalStorage(key, initialValue) {
  const prevKeyRef = useRef(key);

  const readValue = useCallback(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const item = window.localStorage.getItem(key);
      if (item === null || item === undefined) return initialValue;
      return JSON.parse(item);
    } catch (err) {
      console.warn(`[useLocalStorage] Erro ao ler chave "${key}":`, err);
      return initialValue;
    }
  }, [key, initialValue]);

  const [value, setValue] = useState(readValue);

  // Quando a chave muda (ex: virada de dia ou mudança de semana), sincroniza com a nova chave
  useEffect(() => {
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      setValue(readValue());
    }
  }, [key, readValue]);

  // Grava as alterações da chave atual
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (prevKeyRef.current !== key) return; // Evita salvar valor desatualizado na nova chave
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      if (err?.name === "QuotaExceededError") {
        console.error(`[useLocalStorage] Armazenamento local cheio ao gravar "${key}".`);
      } else {
        console.warn(`[useLocalStorage] Erro ao gravar chave "${key}":`, err);
      }
    }
  }, [key, value]);

  const reset = useCallback(() => setValue(initialValue), [initialValue]);

  return [value, setValue, reset];
}
